/**
 * Auto Q&A — Anlass, Kette, Auftrag als reine Fachlogik (Paket 19, Stufe 5).
 *
 * Motor, Service und Oberfläche rufen dasselbe; Tests brauchen keine DB. Die
 * Prüfbedingungen in `schema.ts` spiegeln die Listen von hier — wer eine
 * Zustandsliste ändert, ändert beide, sonst nimmt die Datenbank etwas an, das
 * die Fachlogik nicht kennt.
 *
 * Der Auftrag klammert Durchsichtläufe, er ersetzt sie nicht: Was ein Lauf
 * gefunden hat, steht in `befunde`; was der Auftrag sagt, ist die Auskunft
 * darüber, ob die Kette durchlief und ob etwas aufhält.
 */
import { BLOCKIERENDE_SCHWERE } from "./durchsicht-befund.js";
import { GITLAB_ANLAESSE, QA_ANLASS_DEFINITIONEN, QA_ANLASS_LABEL, istQaAnlass, qaAnlassDefinition, zielartenFuerQaAnlass, } from "./auto-qa-anlaesse.js";
import { beschreibeQaBedingungen, leereQaBedingungsgruppe, pruefeQaBedingungen, } from "./auto-qa-bedingungen.js";
export { GITLAB_ANLAESSE, QA_ANLAESSE, QA_ANLASS_DEFINITIONEN, QA_ANLASS_GRUPPEN, QA_ANLASS_LABEL, QA_ANLASS_QUELLEN, QA_ZEITPLAN_INTERVALLE, QA_ZIELART_LABEL, QA_ZIELARTEN, istGitlabAnlass, naechsterQaZeitplanLauf, qaAnlassDefinition, zielartenFuerQaAnlass, } from "./auto-qa-anlaesse.js";
export function istAnlass(anlass) {
    return istQaAnlass(anlass);
}
/**
 * Worauf ein Auftrag läuft.
 *
 * Nicht `OBJEKTARTEN` aus `kanten.ts`: Dort fehlt `auslieferung`, und die
 * übrigen zwölf Arten kann kein Anlass dieses Pakets erzeugen. Eine Liste, die
 * mehr zulässt als vorkommen kann, prüft nichts.
 */
/** Welches Ziel ein Anlass trägt. Der Motor braucht das vor dem Anlegen. */
export const ANLASS_ZIELART = Object.fromEntries(QA_ANLASS_DEFINITIONEN.filter((definition) => definition.quelle === "gitlab").map((definition) => [definition.schluessel, definition.zielArten[0] ?? "projekt"]));
/* -------------------------------------------------------------------------
 * Automation und Schritt
 * ---------------------------------------------------------------------- */
export const AUTOMATION_ZUSTAENDE = ["entwurf", "aktiv", "pausiert"];
/** A4 — wann der nächste Schritt läuft. */
export const FORTSETZEN_WENN = [
    "immer",
    "ohne_aufhalter",
    "ohne_fehler",
    "nur_bei_aufhalter",
];
/**
 * Vorgabe für neue Schritte.
 *
 * Eine Kette, die nach einem Anbieterausfall munter weiterläuft, verschwendet
 * Marken auf einer falschen Grundlage.
 */
export const FORTSETZEN_WENN_VORGABE = "ohne_fehler";
/** Höchstens zehn Schritte je Automation — siehe Abschnitt D. */
export const QA_SCHRITTE_HOECHSTENS = 10;
/**
 * Beruhigungsfrist in Sekunden.
 *
 * `mr_aktualisiert` feuert bei jedem Push in den Zweig. Ohne Bremse läuft die
 * Kette zwanzigmal am Nachmittag. Ein zweiter Anlass am selben Objekt
 * innerhalb der Frist verlängert sie, statt einen zweiten Auftrag zu erzeugen.
 *
 * Der Wert ist gesetzt, nicht gemessen (offene Frage 2 des Pakets). Nach zwei
 * Wochen Betrieb am tatsächlichen Push-Rhythmus prüfen.
 */
export const BERUHIGUNG_SEKUNDEN_VORGABE = 180;
/**
 * Markenbudget je Auftrag — **vorläufig**.
 *
 * Hergeleitet, nicht gemessen: `schneideDiff` läuft mit `markenGrenze: 24_000`
 * je Durchsichtlauf, also liegt die Obergrenze eines zehnschrittigen Auftrags
 * bei 240 000 Marken Eingang. 150 000 deckt den Eingang von rund sechs vollen
 * Schritten samt Ausgang.
 *
 * Zu ersetzen, sobald `durchsicht_laeufe.markenEin`/`markenAus` über zwei
 * Wochen echte Läufe getragen haben — das ist offene Frage 3 des Pakets, und
 * sie bleibt offen, bis eine Zahl aus Messung statt aus Herleitung kommt.
 */
export const BUDGET_MARKEN_VORGABE = 150_000;
/**
 * Ohne Fortschritt gilt ein Auftrag nach dieser Zeit als gescheitert (G6).
 *
 * Deckt sich mit der Lease der Durchsicht-Worker: Was dort freigegeben wird,
 * darf hier nicht weiter als „läuft" gelten.
 */
export const QA_FORTSCHRITT_FRIST_MS = 15 * 60 * 1_000;
/* -------------------------------------------------------------------------
 * A5 — Zustände
 * ---------------------------------------------------------------------- */
export const QA_AUFTRAG_ZUSTAENDE = [
    "gebremst",
    "wartet",
    "laeuft",
    "fertig",
    "fertig_mit_aufhaltern",
    "gescheitert",
    "abgebrochen",
];
export const QA_AUFTRAG_ENDZUSTAENDE = [
    "fertig",
    "fertig_mit_aufhaltern",
    "gescheitert",
    "abgebrochen",
];
/** Ein Auftrag, der noch etwas vorhat. Die Übersicht fragt nur dann nach. */
export function istOffen(zustand) {
    return !QA_AUFTRAG_ENDZUSTAENDE.includes(zustand);
}
const AUFTRAG_ERLAUBT = {
    gebremst: ["wartet", "abgebrochen"],
    wartet: ["laeuft", "abgebrochen"],
    laeuft: [
        "fertig",
        "fertig_mit_aufhaltern",
        "gescheitert",
        "abgebrochen",
    ],
    fertig: [],
    fertig_mit_aufhaltern: [],
    gescheitert: [],
    abgebrochen: [],
};
export function uebergangAuftrag(von, nach) {
    if (!AUFTRAG_ERLAUBT[von].includes(nach)) {
        throw new Error(`Ungültiger Übergang ${von} → ${nach}`);
    }
    return nach;
}
export const QA_SCHRITT_ZUSTAENDE = [
    "ausstehend",
    "laeuft",
    "fertig",
    "gescheitert",
    "uebersprungen",
];
/**
 * Was ein abgeschlossener Schritt über seine Prüfung sagt.
 *
 * `nicht_bewertbar` ist kein Zwischending zwischen sauber und Aufhalter,
 * sondern die Aussage, dass gar nicht geprüft wurde — Anbieter weg, Diff
 * gekürzt, Budget gerissen.
 */
export const QA_SCHRITT_ERGEBNISSE = [
    "sauber",
    "aufhalter",
    "nicht_bewertbar",
];
export const QA_UEBERSPRUNGEN_GRUENDE = [
    "fortsetzung_nicht_erfuellt",
    "uebersprungen_budget",
    "auftrag_abgebrochen",
    "rezept_fehlt",
];
/* -------------------------------------------------------------------------
 * Aufhalter
 * ---------------------------------------------------------------------- */
/**
 * Ein Aufhalter ist ein **offener** Befund der Schwere `schwer` oder
 * `kritisch`. Ein übernommener oder abgelehnter Befund hält nichts mehr auf —
 * sonst zeigt die Übersicht Rückstand, den jemand längst abgeräumt hat.
 */
export function istAufhalter(schwere, befundZustand) {
    return befundZustand === "offen" && BLOCKIERENDE_SCHWERE.includes(schwere);
}
export function zaehleAufhalter(befunde) {
    return befunde.filter((b) => istAufhalter(b.schwere, b.zustand)).length;
}
/**
 * „Nicht bewertbar" schlägt „sauber".
 *
 * Paket 18 hat den Satz schon formuliert: *Ein Gate darf nie grün werden, wenn
 * der Anbieter gescheitert ist.* Ein gekürzter Diff ist derselbe Fall eine
 * Ebene tiefer — was nicht gelesen wurde, kann nicht sauber sein.
 */
export function bewerteSchritt(lage) {
    if (lage.laufGescheitert || lage.gekuerzt)
        return "nicht_bewertbar";
    return lage.aufhalter > 0 ? "aufhalter" : "sauber";
}
/**
 * Läuft der nächste Schritt, gemessen am zuletzt **gelaufenen**?
 *
 * `voriger === null` heißt: Es ist der erste Schritt. Der läuft immer — seine
 * Fortsetzungsbedingung beschreibt, was nach ihm gilt, nicht vor ihm.
 */
export function schrittLaeuft(fortsetzenWenn, voriger) {
    if (voriger === null)
        return true;
    const gescheitert = voriger.zustand === "gescheitert" || voriger.ergebnis === "nicht_bewertbar";
    const aufhalter = voriger.ergebnis === "aufhalter";
    switch (fortsetzenWenn) {
        case "immer":
            return true;
        case "ohne_fehler":
            return !gescheitert;
        case "ohne_aufhalter":
            return !gescheitert && !aufhalter;
        case "nur_bei_aufhalter":
            return !gescheitert && aufhalter;
        default:
            return false;
    }
}
/** Warum ein Schritt nicht lief — im Klartext, nicht als Code (C2). */
export function uebersprungenText(grund) {
    switch (grund) {
        case "fortsetzung_nicht_erfuellt":
            return "Übersprungen — die Fortsetzungsbedingung des vorigen Schritts griff nicht.";
        case "uebersprungen_budget":
            return "Übersprungen — das Markenbudget des Auftrags war aufgebraucht.";
        case "auftrag_abgebrochen":
            return "Übersprungen — der Auftrag wurde abgebrochen.";
        case "rezept_fehlt":
            return "Übersprungen — das Rezept dieses Schritts gibt es nicht mehr.";
    }
}
/* -------------------------------------------------------------------------
 * A6 — Budget
 * ---------------------------------------------------------------------- */
/**
 * Reißt das Budget?
 *
 * Geprüft **vor** dem nächsten Schritt, nicht mitten in ihm: Der laufende
 * Schritt läuft zu Ende, der Rest wird übersprungen. Ein Schritt, der auf
 * halber Strecke abgeschnitten wird, hinterlässt einen Durchsichtlauf ohne
 * Ergebnis — und das wäre ein Loch im Register, kein gespartes Geld.
 *
 * `budget === null` heißt: keine Grenze gesetzt.
 */
export function budgetGerissen(verbraucht, budget) {
    if (budget === null)
        return false;
    return verbraucht >= budget;
}
/* -------------------------------------------------------------------------
 * A5 — Endzustand des Auftrags
 * ---------------------------------------------------------------------- */
/**
 * Der Endzustand ergibt sich aus den Schritten, nicht aus einer Zählung.
 *
 * `fertig_mit_aufhaltern` ist ein eigener Endzustand, kein `fertig` mit
 * Zusatz: Der Unterschied zwischen „durchgelaufen" und „durchgelaufen, und es
 * hält etwas auf" ist die ganze Auskunft dieser Seite.
 */
export function auftragsEndzustand(schritte) {
    if (schritte.some((s) => s.zustand === "gescheitert" || s.ergebnis === "nicht_bewertbar")) {
        return "gescheitert";
    }
    if (schritte.some((s) => s.ergebnis === "aufhalter")) {
        return "fertig_mit_aufhaltern";
    }
    return "fertig";
}
/**
 * Lief überhaupt ein Schritt? (G5)
 *
 * Eine Kette, in der jede Fortsetzungsbedingung griff, endet in `fertig` —
 * ohne diesen Satz sähe das aus wie eine bestandene Prüfung.
 */
export function keinSchrittLief(schritte) {
    return (schritte.length > 0 &&
        schritte.every((s) => s.zustand === "uebersprungen"));
}
/**
 * Mängel einer Automation. Leer heißt gültig.
 *
 * Abweisen, nicht ignorieren — so hält es `pruefeRegel` bereits, und ein
 * zweiter Umgang mit ungültiger Konfiguration im selben Haus wäre eine Falle.
 */
export function pruefeAutomation(automation) {
    const maengel = [];
    if (!automation.name.trim())
        maengel.push("Name fehlt.");
    if (automation.name.trim().length > 200) {
        maengel.push("Name darf höchstens 200 Zeichen haben.");
    }
    if (!automation.projektId)
        maengel.push("Projekt fehlt.");
    if (!istAnlass(automation.anlass)) {
        maengel.push("Unbekannter Anlass wird abgewiesen, nicht ignoriert.");
    }
    if (automation.anlass === "zeitplan_ausgeloest" && !automation.zeitplan) {
        maengel.push("Ein Zeitplan-Anlass braucht einen Zeitplan.");
    }
    if (automation.anlass !== "zeitplan_ausgeloest" && automation.zeitplan) {
        maengel.push("Ein Zeitplan ist nur beim Anlass „Nach Zeitplan ausgelöst“ erlaubt.");
    }
    if (automation.zeitplan) {
        const zeitplan = automation.zeitplan;
        if (!["stuendlich", "taeglich", "woechentlich"].includes(zeitplan.intervall)) {
            maengel.push("Der Zeitplan hat ein unbekanntes Intervall.");
        }
        if (!Number.isInteger(zeitplan.minute) || zeitplan.minute < 0 || zeitplan.minute > 59) {
            maengel.push("Die Zeitplanminute liegt zwischen 0 und 59.");
        }
        if (!Number.isInteger(zeitplan.stunde) || zeitplan.stunde < 0 || zeitplan.stunde > 23) {
            maengel.push("Die Zeitplanstunde liegt zwischen 0 und 23.");
        }
        if (zeitplan.intervall === "woechentlich" &&
            (!Number.isInteger(zeitplan.wochentag) || (zeitplan.wochentag ?? 0) < 1 || (zeitplan.wochentag ?? 0) > 7)) {
            maengel.push("Ein wöchentlicher Zeitplan braucht einen Wochentag von 1 bis 7.");
        }
        try {
            new Intl.DateTimeFormat("de-DE", { timeZone: zeitplan.zeitzone }).format(new Date());
        }
        catch {
            maengel.push("Die Zeitzone des Zeitplans ist ungültig.");
        }
    }
    const bedingungen = automation.bedingungen ?? leereQaBedingungsgruppe();
    maengel.push(...pruefeQaBedingungen(bedingungen, zielartenFuerQaAnlass(automation.anlass)).map((mangel) => mangel.text));
    if (!Number.isInteger(automation.beruhigungSekunden) ||
        automation.beruhigungSekunden < 0 ||
        automation.beruhigungSekunden > 3_600) {
        maengel.push("Die Beruhigungsfrist liegt zwischen 0 und 3600 Sekunden.");
    }
    if (automation.budgetMarken !== null &&
        (!Number.isInteger(automation.budgetMarken) || automation.budgetMarken < 1)) {
        maengel.push("Das Markenbudget ist entweder leer oder eine Zahl ab 1.");
    }
    const schritte = [...automation.schritte].sort((a, b) => a.reihenfolge - b.reihenfolge);
    if (schritte.length === 0) {
        maengel.push("Eine Automation braucht mindestens einen Schritt.");
    }
    if (schritte.length > QA_SCHRITTE_HOECHSTENS) {
        maengel.push(`Eine Automation darf höchstens ${QA_SCHRITTE_HOECHSTENS} Schritte haben.`);
    }
    if (schritte.some((s) => !Number.isInteger(s.reihenfolge) || s.reihenfolge < 0)) {
        maengel.push("Schritt-Reihenfolgen müssen nichtnegative ganze Zahlen sein.");
    }
    if (new Set(schritte.map((s) => s.reihenfolge)).size !== schritte.length) {
        maengel.push("Jede Schritt-Reihenfolge muss eindeutig sein.");
    }
    for (const schritt of schritte) {
        if (!schritt.rezeptId) {
            maengel.push("Jeder Schritt braucht ein Rezept.");
        }
        if (schritt.rezeptFassung !== null &&
            (!Number.isInteger(schritt.rezeptFassung) || schritt.rezeptFassung < 1)) {
            maengel.push("Eine feste Rezeptfassung ist eine Zahl ab 1.");
        }
        if (!FORTSETZEN_WENN.includes(schritt.fortsetzenWenn)) {
            maengel.push("Unbekannte Fortsetzungsbedingung.");
        }
    }
    return maengel;
}
const ANLASS_TEXT = {
    mr_eroeffnet: "GitLab einen neuen Merge Request meldet",
    mr_aktualisiert: "ein Merge Request neue Commits bekommt",
    mr_zum_merge_bereit: "ein Merge Request die Entwurfsmarke verliert",
    pipeline_gescheitert: "eine Pipeline scheitert",
    auslieferung_angelegt: "eine Auslieferung angelegt wird",
};
/** Rückwärtskompatibler GitLab-Ausschnitt des vollständigen Katalogs. */
export const ANLASS_LABEL = Object.fromEntries(GITLAB_ANLAESSE.map((anlass) => [anlass, QA_ANLASS_LABEL[anlass]]));
/**
 * Die Vorschau aus Abschnitt D, in einem Satz.
 *
 * Sie ist die wichtigste Prüfung der Maske: Wer sie nicht laut vorlesen kann,
 * hat die Automation falsch zusammengesteckt. Deshalb steht sie hier und nicht
 * in der Oberfläche — der Satz muss auch im Test entstehen können.
 */
export function vorschauSatz(anlass, schritte, bedingungen = leereQaBedingungsgruppe()) {
    const anlassText = ANLASS_TEXT[anlass] ??
        (istAnlass(anlass)
            ? `„${qaAnlassDefinition(anlass)?.label ?? anlass}" gemeldet wird`
            : `der unbekannte Anlass „${anlass}" eintritt`);
    const bedingungsText = bedingungen.kinder.length > 0
        ? `, sofern ${beschreibeQaBedingungen(bedingungen)}`
        : "";
    if (schritte.length === 0) {
        return `Sobald ${anlassText}${bedingungsText}: nichts — die Automation hat keinen Schritt.`;
    }
    const teile = schritte.map((schritt, index) => {
        if (index === 0)
            return schritt.rezeptName;
        switch (schritt.fortsetzenWenn) {
            case "immer":
                return `danach immer ${schritt.rezeptName}`;
            case "ohne_aufhalter":
                return `danach — falls nichts aufhält — ${schritt.rezeptName}`;
            case "ohne_fehler":
                return `danach — falls nichts scheiterte — ${schritt.rezeptName}`;
            case "nur_bei_aufhalter":
                return `danach — nur falls etwas aufhält — ${schritt.rezeptName}`;
        }
    });
    return `Sobald ${anlassText}${bedingungsText}: ${teile.join(", ")}.`;
}
//# sourceMappingURL=auto-qa.js.map